import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Tag, Plus, Trash2, Users, Power } from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

const CouponsPage = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '', discount_type: 'percentage', discount_value: 0,
    valid_until: '', max_uses_total: 100, max_uses_per_user: 1
  });

  const { data: coupons = [] } = useQuery({
    queryKey: ['coupons'],
    queryFn: () => axiosClient.get('/admin/coupons')
  });
  const { can } = usePermission();

  const createMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/coupons', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['coupons']);
      setShowForm(false);
      // Optional: clear form
      alert("Coupon Created Successfully!");
    },
    // ✅ FIX: Show specific error message from backend
    onError: (err) => {
        const message = err.response?.data?.detail || "Failed to create coupon.";
        alert(`Error: ${message}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/coupons/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['coupons'])
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => axiosClient.put(`/admin/coupons/${id}/toggle`, { is_active }),
    onSuccess: () => queryClient.invalidateQueries(['coupons'])
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      discount_value: parseFloat(formData.discount_value),
      max_uses_total: parseInt(formData.max_uses_total),
      max_uses_per_user: parseInt(formData.max_uses_per_user),
      // If date is set, ensure it's ISO
      valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800">Coupon Manager</h1>
        {can('coupons:manage') && (
            
          <button onClick={() => setShowForm(!showForm)} className="bg-pink-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-pink-700 shadow-lg shadow-pink-500/20">
            <Plus className="w-4 h-4 mr-2" /> New Coupon
          </button>
        )}  
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Coupon Code</label>
              <input className="w-full p-2 border rounded font-mono uppercase" required onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} />
            </div>
            <div className="grid grid-cols-2 gap-2">
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Type</label>
                 <select className="w-full p-2 border rounded" onChange={e => setFormData({...formData, discount_type: e.target.value})}>
                   <option value="percentage">Percentage (%)</option>
                   <option value="fixed_amount">Fixed Amount ($)</option>
                 </select>
               </div>
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Value</label>
                 <input type="number" className="w-full p-2 border rounded" required onChange={e => setFormData({...formData, discount_value: e.target.value})} />
               </div>
            </div>
            
            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Expiry Date</label>
               <input type="date" className="w-full p-2 border rounded" onChange={e => setFormData({...formData, valid_until: e.target.value})} />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Total Uses</label>
                 <input type="number" className="w-full p-2 border rounded" defaultValue={100} onChange={e => setFormData({...formData, max_uses_total: e.target.value})} />
               </div>
               <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Per User</label>
                 <input type="number" className="w-full p-2 border rounded" defaultValue={1} onChange={e => setFormData({...formData, max_uses_per_user: e.target.value})} />
               </div>
            </div>

            <div className="md:col-span-2 flex justify-end space-x-3 pt-2">
               <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded">Cancel</button>
               <button type="submit" className="px-6 py-2 bg-pink-600 text-white rounded hover:bg-pink-700">Create Coupon</button>
            </div>
          </form>
        </div>
      )}

      {/* Coupon List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Code</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Discount</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Usage</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Validity</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {coupons.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50/50">
                <td className="px-6 py-4 font-mono font-bold text-slate-700 flex items-center">
                   <Tag className="w-4 h-4 mr-2 text-pink-500" /> {c.code}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-slate-600">
                   {c.discount_type === 'percentage' ? `${c.discount_value}% OFF` : `$${c.discount_value} OFF`}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500 flex items-center">
                   <Users className="w-3 h-3 mr-1" /> {c.current_uses} / {c.max_uses_total || '∞'}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                   {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : 'Forever'}
                </td>
                <td className="px-6 py-4 text-right flex items-center justify-end space-x-2">
                   <button 
                     onClick={() => toggleMutation.mutate({ id: c.id, is_active: !c.is_active })}
                     className={`p-1.5 rounded-md ${c.is_active ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}
                   >
                     <Power className="w-4 h-4" />
                   </button>
                   <button 
                     onClick={() => deleteMutation.mutate(c.id)}
                     className="p-1.5 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500"
                   >
                     <Trash2 className="w-4 h-4" />
                   </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CouponsPage;